import React, { useState } from 'react';
import './Login.css';

const Login = ({ onLogin }) => {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showSetPassword, setShowSetPassword] = useState(false);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [newAdminUsername, setNewAdminUsername] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!identifier) {
      setError('Please enter email/username');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch('https://main.victriedemos.com:3017/api/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          identifier,
          password
        })
      });

      const data = await response.json();

      // Check if new admin needs to set password
      if (data.requirePasswordSetup) {
        setNewAdminEmail(data.email);
        setNewAdminUsername(data.username);
        setShowSetPassword(true);
        setError('');
        setLoading(false);
        return;
      }

      if (data.success) {
        // Store user data in localStorage
        localStorage.setItem('isLoggedIn', 'true');
        localStorage.setItem('username', data.user.username);
        localStorage.setItem('userEmail', data.user.email);
        localStorage.setItem('userRole', data.user.role);

        // Store email if remember me is checked
        if (rememberMe) {
          localStorage.setItem('rememberMe', 'true');
          localStorage.setItem('savedIdentifier', identifier);
        } else {
          localStorage.removeItem('rememberMe');
          localStorage.removeItem('savedIdentifier');
        }

        // Call parent login handler with user data
        if (onLogin) {
          onLogin(data.user);
        }
      } else {
        setError(data.message);
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Unable to connect to server. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setResetMessage('');
    setLoading(true);

    if (!resetEmail) {
      setError('Please enter your email address');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch('https://main.victriedemos.com:3017/api/forgot-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: resetEmail })
      });

      const data = await response.json();

      if (data.success) {
        setResetMessage(data.message);
        // Show password reset form
        setTimeout(() => {
          setShowForgotPassword(false);
          setShowResetPassword(true);
        }, 2000);
      } else {
        setError(data.message);
      }
    } catch (err) {
      console.error('Forgot password error:', err);
      setError('Unable to connect to server. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setResetMessage('');
    setLoading(true);

    if (!newPassword || !confirmPassword) {
      setError('Please enter both password fields');
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch('https://main.victriedemos.com:3017/api/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: resetEmail,
          newPassword
        })
      });

      const data = await response.json();

      if (data.success) {
        setResetMessage(data.message);
        setTimeout(() => {
          setShowResetPassword(false);
          setResetEmail('');
          setNewPassword('');
          setConfirmPassword('');
        }, 2000);
      } else {
        setError(data.message);
      }
    } catch (err) {
      console.error('Reset password error:', err);
      setError('Unable to connect to server. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleSetInitialPassword = async (e) => {
    e.preventDefault();
    setError('');
    setResetMessage('');
    setLoading(true);

    if (!newPassword || !confirmPassword) {
      setError('Please enter both password fields');
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch('https://main.victriedemos.com:3017/api/set-initial-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: newAdminEmail,
          newPassword
        })
      });

      const data = await response.json();

      if (data.success) {
        setResetMessage(data.message);
        setTimeout(() => {
          setShowSetPassword(false);
          setNewAdminEmail('');
          setNewAdminUsername('');
          setNewPassword('');
          setConfirmPassword('');
          setIdentifier('');
          setPassword('');
        }, 2000);
      } else {
        setError(data.message);
      }
    } catch (err) {
      console.error('Set password error:', err);
      setError('Unable to connect to server. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  if (showSetPassword) {
    return (
      <div className="login-container">
        <div className="login-background">
          <div className="shape"></div>
          <div className="shape"></div>
        </div>
        
        <div className="login-card">
          <div className="login-header">
            <div className="logo">
              <div className="logo-icon">🛡️</div>
            </div>
            <h1>Set Your Password</h1>
            <p>Welcome {newAdminUsername}! Please set your password to activate your account.</p>
          </div>

          <form onSubmit={handleSetInitialPassword} className="login-form">
            {error && <div className="error-message">{error}</div>}
            {resetMessage && <div className="success-message">{resetMessage}</div>}
            
            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                type="password"
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 6 characters)"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                disabled={loading}
              />
            </div>

            <button type="submit" className="login-button" disabled={loading}>
              {loading ? 'Setting Password...' : 'Set Password'}
            </button>

            <button 
              type="button" 
              className="back-button"
              onClick={() => {
                setShowSetPassword(false);
                setNewAdminEmail('');
                setNewAdminUsername('');
                setNewPassword('');
                setConfirmPassword('');
                setError('');
                setResetMessage('');
              }}
            >
              Back to Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (showForgotPassword) {
    return (
      <div className="login-container">
        <div className="login-background">
          <div className="shape"></div>
          <div className="shape"></div>
        </div>
        
        <div className="login-card">
          <div className="login-header">
            <div className="logo">
              <div className="logo-icon">🛡️</div>
            </div>
            <h1>Forgot Password</h1>
            <p>Enter your email to reset password</p>
          </div>

          <form onSubmit={handleForgotPassword} className="login-form">
            {error && <div className="error-message">{error}</div>}
            {resetMessage && <div className="success-message">{resetMessage}</div>}
            
            <div className="form-group">
              <label htmlFor="resetEmail">Email Address</label>
              <input
                type="email"
                id="resetEmail"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder="Enter your email"
                disabled={loading}
              />
            </div>

            <button type="submit" className="login-button" disabled={loading}>
              {loading ? 'Processing...' : 'Send Reset Link'}
            </button>

            <button 
              type="button" 
              className="back-button"
              onClick={() => {
                setShowForgotPassword(false);
                setError('');
                setResetMessage('');
              }}
            >
              Back to Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (showResetPassword) {
    return (
      <div className="login-container">
        <div className="login-background">
          <div className="shape"></div>
          <div className="shape"></div>
        </div>
        
        <div className="login-card">
          <div className="login-header">
            <div className="logo">
              <div className="logo-icon">🛡️</div>
            </div>
            <h1>Reset Password</h1>
            <p>Enter your new password</p>
          </div>

          <form onSubmit={handleResetPassword} className="login-form">
            {error && <div className="error-message">{error}</div>}
            {resetMessage && <div className="success-message">{resetMessage}</div>}
            
            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                type="password"
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                disabled={loading}
              />
            </div>

            <button type="submit" className="login-button" disabled={loading}>
              {loading ? 'Updating...' : 'Update Password'}
            </button>

            <button 
              type="button" 
              className="back-button"
              onClick={() => {
                setShowResetPassword(false);
                setShowForgotPassword(false);
                setError('');
                setResetMessage('');
                setNewPassword('');
                setConfirmPassword('');
              }}
            >
              Back to Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-background">
        <div className="shape"></div>
        <div className="shape"></div>
      </div>
      
      <div className="login-card">
        <div className="login-header">
          <div className="logo">
            <div className="logo-icon">🛡️</div>
          </div>
          <h1>Zakim Insurance</h1>
          <p>Your Trusted Insurance Partner</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && <div className="error-message">{error}</div>}
          
          <div className="form-group">
            <label htmlFor="identifier">Email or Username</label>
            <input
              type="text"
              id="identifier"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder="Enter your email or username"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              autoComplete="current-password"
              disabled={loading}
            />
          </div>

          <div className="form-options">
            <label className="remember-me">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                disabled={loading}
              />
              <span>Remember me</span>
            </label>
            <a 
              href="#forgot" 
              className="forgot-password"
              onClick={(e) => {
                e.preventDefault();
                setShowForgotPassword(true);
                setError('');
              }}
            >
              Forgot Password?
            </a>
          </div>

          <button type="submit" className="login-button" disabled={loading}>
            {loading ? 'Signing In...' : 'Sign In'}
          </button>
        </form>

        <div className="login-footer">
          <p>Don't have an account? <a href="#signup">Sign Up</a></p>
        </div>
      </div>
    </div>
  );
};

export default Login;
